import React, { useRef, useState } from "react";
import { PhotoRef } from "../../types/Inventory";
import { photoUrl } from "../../api/InventoryAPI";
import { logApiError, getUserMessage } from "../../api/apiError";

interface Props {
    inventoryId: string;
    photos: PhotoRef[];
    /** Upload one photo (bumps version + reloads). */
    onUpload: (file: File) => Promise<void>;
    onDelete: (photoId: string) => Promise<void>;
    onReorder: (photoIdsInOrder: string[]) => Promise<void>;
    disabled?: boolean;
}

/** Manages a listing's photos as individual assets: add, remove, reorder. */
const PhotosManager: React.FC<Props> = ({ inventoryId, photos, onUpload, onDelete, onReorder, disabled }) => {
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const sorted = [...photos].sort((a, b) => a.order - b.order);
    const busyOrDisabled = busy || !!disabled;

    const run = async (fn: () => Promise<void>) => {
        setBusy(true);
        setError(null);
        try {
            await fn();
        } catch (err) {
            logApiError(err);
            setError(getUserMessage(err));
        } finally {
            setBusy(false);
        }
    };

    const addFiles = (files: FileList | null) => {
        if (!files || files.length === 0) return;
        // Upload sequentially — each upload bumps the version and reloads the item.
        run(async () => {
            for (const f of Array.from(files)) {
                if (!f.type.startsWith("image/")) continue;
                await onUpload(f);
            }
        });
    };

    const move = (i: number, dir: -1 | 1) => {
        const j = i + dir;
        if (j < 0 || j >= sorted.length) return;
        const ids = sorted.map((p) => p.photoId);
        [ids[i], ids[j]] = [ids[j], ids[i]];
        run(() => onReorder(ids));
    };

    return (
        <div style={{ border: "1px solid var(--border, #ddd)", borderRadius: "6px", padding: "0.6rem", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                multiple
                style={{ display: "none" }}
                onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
                disabled={busyOrDisabled}
            />
            <button type="button" onClick={() => inputRef.current?.click()} disabled={busyOrDisabled} style={{ alignSelf: "flex-start" }}>
                {busy ? "Working…" : "Add photos"}
            </button>

            {sorted.length > 0 ? (
                <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                    {sorted.map((p, i) => (
                        <div key={p.photoId} style={{ width: "96px" }}>
                            <img
                                src={photoUrl(inventoryId, p.photoId)}
                                alt=""
                                style={{ width: "96px", height: "96px", objectFit: "cover", borderRadius: "4px", display: "block" }}
                            />
                            <div style={{ display: "flex", justifyContent: "space-between", marginTop: "2px" }}>
                                <button type="button" className="btn-sm" onClick={() => move(i, -1)} disabled={busyOrDisabled || i === 0} title="Move left">←</button>
                                <button type="button" className="btn-sm" onClick={() => run(() => onDelete(p.photoId))} disabled={busyOrDisabled} title="Remove">✕</button>
                                <button type="button" className="btn-sm" onClick={() => move(i, 1)} disabled={busyOrDisabled || i === sorted.length - 1} title="Move right">→</button>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <small style={{ color: "var(--text-secondary)" }}>No photos yet — add some.</small>
            )}

            {error && <div className="error-message" style={{ marginTop: "0.25rem" }}>{error}</div>}
        </div>
    );
};

export default PhotosManager;
